/**
 * Gregorian ⇄ Jalali (Solar Hijri) conversion.
 *
 * The backend speaks Gregorian ISO dates everywhere except `MonthlyCharge`,
 * which carries an explicit Jalali `year`/`month` pair. The whole UI is Jalali,
 * so conversion happens exactly once — here — and every screen formats through
 * `formatters.ts`.
 *
 * Implements the arithmetic of the Birashk/Borkowski algorithm as popularised
 * by `jalaali-js`, which is exact for 1178–1633 Jalali. Written in-repo rather
 * than pulled in as a dependency: it is ~80 lines and has no release cadence
 * worth tracking.
 */

const BREAKS = [
  -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394,
  2456, 3178,
] as const;

const div = (a: number, b: number): number => Math.trunc(a / b);
const mod = (a: number, b: number): number => a - Math.trunc(a / b) * b;

export interface JalaliDate {
  /** Jalali year, e.g. 1404. */
  year: number;
  /** Jalali month, 1–12. */
  month: number;
  /** Day of month, 1–31. */
  day: number;
}

export interface GregorianDate {
  year: number;
  /** 1–12 (not the JS 0-based month). */
  month: number;
  day: number;
}

interface JalaliCalendar {
  /**
   * Years elapsed since the last leap year, 0–4. Zero means *this* year is a
   * leap year; one means the year before it was. Both readings are used below,
   * which is why the raw value is kept rather than a boolean.
   */
  yearsSinceLastLeap: number;
  gregorianYear: number;
  /** Day of March on which the Jalali year starts. */
  march: number;
}

/** Calendar constants for a Jalali year: leap flag and Nowruz offset. */
function jalaliCalendar(jy: number): JalaliCalendar {
  const gy = jy + 621;
  let leapJ = -14;
  // Annotated: the `as const` table would otherwise narrow this to the literal
  // -61 and reject every later assignment.
  let jp: number = BREAKS[0];
  let jump = 0;

  if (jy < jp || jy >= BREAKS[BREAKS.length - 1]!) {
    throw new RangeError(`Jalali year out of supported range: ${jy}`);
  }

  for (let i = 1; i < BREAKS.length; i += 1) {
    const jm = BREAKS[i]!;
    jump = jm - jp;
    if (jy < jm) break;
    leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }

  let n = jy - jp;
  leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1;

  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
  const march = 20 + leapJ - leapG;

  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33;
  let yearsSinceLastLeap = mod(mod(n + 1, 33) - 1, 4);
  if (yearsSinceLastLeap === -1) yearsSinceLastLeap = 4;

  return { yearsSinceLastLeap, gregorianYear: gy, march };
}

/** Whether a Jalali year has 366 days (Esfand runs to 30). */
export function isLeapJalaliYear(year: number): boolean {
  return jalaliCalendar(year).yearsSinceLastLeap === 0;
}

/** Gregorian date → Julian Day Number. */
function gregorianToJdn({ year, month, day }: GregorianDate): number {
  let d =
    div((year + div(month - 8, 6) + 100100) * 1461, 4) +
    div(153 * mod(month + 9, 12) + 2, 5) +
    day -
    34840408;
  d = d - div(div(year + 100100 + div(month - 8, 6), 100) * 3, 4) + 752;
  return d;
}

/** Julian Day Number → Gregorian date. */
function jdnToGregorian(jdn: number): GregorianDate {
  let j = 4 * jdn + 139361631;
  j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const day = div(mod(i, 153), 5) + 1;
  const month = mod(div(i, 153), 12) + 1;
  const year = div(j, 1461) - 100100 + div(8 - month, 6);
  return { year, month, day };
}

export function toJalali(date: Date): JalaliDate {
  const jdn = gregorianToJdn({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
  return jdnToJalali(jdn);
}

export function toGregorian({ year, month, day }: JalaliDate): Date {
  const jdn = jalaliToJdn(year, month, day);
  const g = jdnToGregorian(jdn);
  return new Date(g.year, g.month - 1, g.day);
}

function jalaliToJdn(jy: number, jm: number, jd: number): number {
  const r = jalaliCalendar(jy);
  return (
    gregorianToJdn({ year: r.gregorianYear, month: 3, day: r.march }) +
    (jm - 1) * 31 -
    div(jm, 7) * (jm - 7) +
    jd -
    1
  );
}

function jdnToJalali(jdn: number): JalaliDate {
  const gy = jdnToGregorian(jdn).year;
  let jy = gy - 621;
  const r = jalaliCalendar(jy);
  const firstDayOfYear = gregorianToJdn({ year: r.gregorianYear, month: 3, day: r.march });
  let k = jdn - firstDayOfYear;

  if (k >= 0) {
    if (k <= 185) {
      return { year: jy, month: 1 + div(k, 31), day: mod(k, 31) + 1 };
    }
    k -= 186;
  } else {
    // The JDN falls in the previous Jalali year; 179 is the day count of its
    // months 7–12, plus one more when that year was a leap year.
    jy -= 1;
    k += 179;
    if (r.yearsSinceLastLeap === 1) k += 1;
  }

  return { year: jy, month: 7 + div(k, 30), day: mod(k, 30) + 1 };
}

/** Number of days in a given Jalali month. */
export function jalaliMonthLength(year: number, month: number): number {
  if (month <= 6) return 31;
  if (month <= 11) return 30;
  return isLeapJalaliYear(year) ? 30 : 29;
}

export const JALALI_MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

export function jalaliMonthName(month: number): string {
  return JALALI_MONTH_NAMES[month - 1] ?? '';
}

/** Today's date in the Jalali calendar. */
export function todayJalali(): JalaliDate {
  return toJalali(new Date());
}

/**
 * Walk back `count` Jalali months from `from`, oldest first. Used to build the
 * month axis of the dashboard charts and the monthly-charge period picker.
 */
export function lastJalaliMonths(
  count: number,
  from: Pick<JalaliDate, 'year' | 'month'> = todayJalali(),
): Array<Pick<JalaliDate, 'year' | 'month'>> {
  const months: Array<Pick<JalaliDate, 'year' | 'month'>> = [];
  let { year, month } = from;
  for (let i = 0; i < count; i += 1) {
    months.unshift({ year, month });
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }
  return months;
}
